import { gql } from "@apollo/client";

// Authentication query
export const GET_MY_AUTH = gql`
  query GetMyAuth {
    me
    eventId
    event {
      id
      name
      roles
    }
    standId
    company
    name
    allowed
  }
`;

// Mutation to upload scan result
export const UPLOAD_SCAN_RESULT = gql`
  mutation UploadScanResult($id: String!, $role: String!, $inp: String!) {
    uploadScanResult(id: $id, role: $role, inp: $inp)
  }
`;

// Mutation to check for duplicate scans
export const CHECK_DUPLICATE_ID = gql`
  mutation CheckDuplicateId($id: String!, $role: String!) {
    checkDuplicateId(id: $id, role: $role)
  }
`;

// Query to get visitor analytics
export const GET_VISITOR_STATS = gql`
  query GetVisitorStats {
    visitorStats {
      total
      today
      byType {
        type
        count
      }
    }
  }
`;

// Query to get scanned visitors
export const GET_SCANNED_VISITORS = gql`
  query GetScannedVisitors {
    scannedVisitors {
      id
      role
      name
      timestamp
      type
      remark
    }
  }
`;

// Query to get staff members
export const GET_STAFF_MEMBERS = gql`
  query GetStaffMembers {
    staffMembers {
      id
      name
      email
      role
      active
    }
  }
`;

// Mutation to add a staff member
export const ADD_STAFF_MEMBER = gql`
  mutation AddStaffMember(
    $name: String!
    $email: String!
    $role: String!
    $password: String!
  ) {
    addStaffMember(
      name: $name
      email: $email
      role: $role
      password: $password
    ) {
      id
      name
      email
      role
    }
  }
`;

// Mutation to update a staff member
export const UPDATE_STAFF_MEMBER = gql`
  mutation UpdateStaffMember(
    $id: String!
    $name: String!
    $email: String!
    $role: String!
    $active: Boolean!
  ) {
    updateStaffMember(
      id: $id
      name: $name
      email: $email
      role: $role
      active: $active
    ) {
      id
      name
      email
      role
      active
    }
  }
`;

// Mutation for login
export const LOGIN = gql`
  mutation Login($eventId: String!, $email: String!, $password: String!) {
    login(eventId: $eventId, email: $email, password: $password)
  }
`;

// Applicants query - updated to match backend schema
export const GET_APPLICANTS = gql`
  query GetApplicants($conditions: InputConditions, $role: String = "all") {
    applicants(conditions: $conditions, role: $role) {
      id
      data
      created
      name
      type
      remark
      role
      standId
      invoicePath
      couponNo
      totalBill
      discountApplied
      grandTotal
      shopName
      shopLocation
      shopContact
      __typename
    }
  }
`;

// Applicants count
export const GET_APPLICANTS_COUNT = gql`
  query GetApplicantsCount($conditions: JSON, $role: String = "all") {
    applicantsCount(conditions: $conditions, role: $role)
  }
`;

// Get event list
export const GET_EVENT_LIST = gql`
  query GetEventList {
    getEventList {
      id
      name
    }
  }
`;

// Stands query
export const GET_STANDS = gql`
  query GetStands($eventId: String!) {
    stands(eventId: $eventId) {
      id
      email
      company
      name
      parentId
      role
      created
      allowed_no_of_staff
    }
  }
`;

// Use getStandsInsights instead of getInsights
export const GET_INSIGHTS = gql`
  query ($eventId: String!, $standId: String!) {
    getInsights(eventId: $eventId, standId: $standId)
  }
`;

// Form fields queries
export const GET_SELECTED_FORM_FIELDS = gql`
  query GetSelectedFormField($eventId: String!) {
    getSelectedFormField(eventId: $eventId)
  }
`;

export const GET_ALL_FORM_FIELDS = gql`
  query GetAllFormField($eventId: String!) {
    getAllFormField(eventId: $eventId)
  }
`;

export const GET_ROLES = gql`
  query GetRoles {
    getRoles
  }
`;

// Coupon queries and mutations
export const CHECK_COUPON_CLAIMED = gql`
  query CheckCouponClaimed($id: String!, $role: String!) {
    checkCouponClaimed(id: $id, role: $role)
  }
`;

export const GENERATE_COUPON_NUMBER = gql`
  mutation GenerateCouponNumber {
    generateCouponNumber
  }
`;

export const SUBMIT_COUPON_CLAIM = gql`
  mutation SubmitCouponClaim(
    $id: String!
    $role: String!
    $couponNo: String!
    $totalBill: Float!
    $discountApplied: Float!
    $grandTotal: Float!
    $invoiceData: String!
    $invoiceFileName: String!
  ) {
    submitCouponClaim(
      id: $id
      role: $role
      couponNo: $couponNo
      totalBill: $totalBill
      discountApplied: $discountApplied
      grandTotal: $grandTotal
      invoiceData: $invoiceData
      invoiceFileName: $invoiceFileName
    )
  }
`;

export const GET_COUPON_PROGRESS = gql`
  query GetCouponProgress {
    getCouponProgress
  }
`;

export const GET_COUPON_CLAIMS = gql`
  query GetCouponClaims {
    getCouponClaims {
      id
      scanName
      couponNo
      totalBill
      discountApplied
      grandTotal
      invoicePath
      createdAt
      shopName
      shopLocation
      shopContact
      eventId
    }
  }
`;

// Email queries and mutations
export const GET_EMAIL_TEMPLATES = gql`
  query GetEmailTemplates($templateType: String) {
    getEmailTemplates(templateType: $templateType) {
      id
      adminId
      templateType
      subject
      body
      created
      updated
    }
  }
`;

export const CREATE_EMAIL_TEMPLATE = gql`
  mutation CreateEmailTemplate($templateType: String!, $subject: String!, $body: String!) {
    createEmailTemplate(templateType: $templateType, subject: $subject, body: $body)
  }
`;

export const UPDATE_EMAIL_TEMPLATE = gql`
  mutation UpdateEmailTemplate($id: String!, $subject: String, $body: String) {
    updateEmailTemplate(id: $id, subject: $subject, body: $body)
  }
`;

export const GET_EMAIL_SETTINGS = gql`
  query GetEmailSettings {
    getEmailSettings {
      id
      adminId
      senderName
      senderEmail
      smtpHost
      smtpPort
      smtpSecure
      created
      updated
    }
  }
`;

export const SAVE_EMAIL_SETTINGS = gql`
  mutation SaveEmailSettings(
    $senderName: String!
    $senderEmail: String!
    $senderPassword: String!
    $smtpHost: String!
    $smtpPort: Int!
    $smtpSecure: Boolean!
  ) {
    saveEmailSettings(
      senderName: $senderName
      senderEmail: $senderEmail
      senderPassword: $senderPassword
      smtpHost: $smtpHost
      smtpPort: $smtpPort
      smtpSecure: $smtpSecure
    )
  }
`;

export const TEST_EMAIL_SETTINGS = gql`
  mutation TestEmailSettings($to: String!) {
    testEmailSettings(to: $to)
  }
`;

export const SEND_COUPON_EMAIL = gql`
  mutation SendCouponEmail(
    $userId: String!
    $role: String!
    $couponNo: String!
    $totalBill: Float!
    $discountApplied: Float!
    $grandTotal: Float!
  ) {
    sendCouponEmail(
      userId: $userId
      role: $role
      couponNo: $couponNo
      totalBill: $totalBill
      discountApplied: $discountApplied
      grandTotal: $grandTotal
    )
  }
`;

export const SEND_BULK_EMAILS = gql`
  mutation SendBulkEmails($attendeeIds: [String]!, $templateType: String!) {
    sendBulkEmails(attendeeIds: $attendeeIds, templateType: $templateType)
  }
`;

export const SEND_BULK_COUPON_EMAILS = gql`
  mutation SendBulkCouponEmails($claims: [CouponClaimInput!]!) {
    sendBulkCouponEmails(claims: $claims)
  }
`;

export const GET_EMAIL_JOBS = gql`
  query GetEmailJobs {
    getEmailJobs {
      id
      adminId
      jobType
      totalEmails
      sentCount
      failedCount
      status
      created
      updated
    }
  }
`;

export const GET_EMAIL_VARIABLES = gql`
  query GetEmailVariables {
    getEmailVariables
  }
`;

// Attendee queries and mutations
export const GET_ATTENDEES = gql`
  query GetAttendees {
    getAttendees {
      id
      email
      name
      shopName
      shopLocation
      shopContact
      role
      created
    }
  }
`;

export const CREATE_ATTENDEE = gql`
  mutation CreateAttendee(
    $email: String!
    $name: String!
    $password: String!
    $shopName: String!
    $shopLocation: String!
    $shopContact: String!
  ) {
    createAttendee(
      email: $email
      name: $name
      password: $password
      shopName: $shopName
      shopLocation: $shopLocation
      shopContact: $shopContact
    )
  }
`;

export const REGISTER_JOTUN_SHOP = gql`
  mutation RegisterJotunShop(
    $email: String!
    $name: String!
    $password: String!
    $shopName: String!
    $shopLocation: String
    $shopContact: String
  ) {
    registerJotunShop(
      email: $email
      name: $name
      password: $password
      shopName: $shopName
      shopLocation: $shopLocation
      shopContact: $shopContact
    )
  }
`;

export const UPLOAD_ATTENDEES = gql`
  mutation UploadAttendees($csvData: String!, $fileName: String) {
    uploadAttendees(csvData: $csvData, fileName: $fileName)
  }
`;

export const GET_ATTENDEE_IMPORTS = gql`
  query GetAttendeeImports {
    getAttendeeImports {
      id
      adminId
      fileName
      totalRecords
      successCount
      failedCount
      importStatus
      errorLog
      created
    }
  }
`;

export const GET_ATTENDEE_IMPORT_DETAIL = gql`
  query GetAttendeeImportDetail($importId: String!) {
    getAttendeeImportDetail(importId: $importId) {
      importId
      successCount
      failedCount
      successRows
      failedRows
    }
  }
`;
